import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, startOfMonth } from 'date-fns'
import { ExportFormat, ExportOption } from '@/modules/exports'
import { useInfringementsMutations } from '@/modules/infringements'
import { useCallback, useMemo } from 'react'
import { useUser } from '@/utils/hooks'
import { Module } from '..'
import { ALL_MODULE_CONFIG_QUERYS_SHALLOW } from '../queries'
import type { InfringementsSettingsForm } from './util/InfringementsSettings'

export type ActiveModuleName = keyof ReturnType<
  typeof useModuleConfigs
>['active']

export function useModuleConfigs(skip: boolean = false) {
  const { data, loading, refetch } = useQuery(
    ALL_MODULE_CONFIG_QUERYS_SHALLOW,
    { skip }
  )

  const modules = useMemo(
    () => data?.allModuleConfigs ?? [],
    [data?.allModuleConfigs]
  )

  const moduleNameModules = useMemo<Record<string, (typeof modules)[number]>>(
    () => Object.fromEntries(modules.map(module => [module.name, module])),
    [modules]
  )

  const moduleNames = useMemo(
    () => modules.map(module => module.name),
    [modules]
  )

  const activeModuleNames = useMemo(
    () => modules.filter(module => module.active).map(module => module.name),
    [modules]
  )

  const active = useMemo(
    () => ({
      activities: activeModuleNames.includes(Module.ACTIVITIES),
      terminal: activeModuleNames.includes(Module.TERMINAL),
      timesheets: activeModuleNames.includes(Module.TIMESHEETS),
      vehicles: activeModuleNames.includes(Module.VEHICLES),
      absences: activeModuleNames.includes(Module.ABSENCES),
      handbook: activeModuleNames.includes(Module.HANDBOOK),
      forum: activeModuleNames.includes(Module.FORUM),
      issues: activeModuleNames.includes(Module.ISSUES),
      infringements: activeModuleNames.includes(Module.INFRINGEMENTS),
      routePlanner: activeModuleNames.includes(Module.ROUTEPLANNER),
      fleet: activeModuleNames.includes(Module.FLEET),
      driverPlanner: activeModuleNames.includes(Module.PLANNER),
      salary: activeModuleNames.includes(Module.SALARY),
    }),
    [activeModuleNames]
  )

  const hasAnyModule = useMemo(
    () => Object.values(active).some(v => v),
    [active]
  )

  return {
    modules,
    moduleNameModules,
    moduleNames,

    loading,

    active,
    hasAnyModule,

    refetch,
  }
}

export function useCompositeModules() {
  const { active } = useModuleConfigs()

  const dashboardStatisticsModuleActive = active.activities || active.issues

  const activitiesHandbookIssuesActive =
    active.activities || active.handbook || active.issues

  const mainModuleActive =
    active.activities ||
    active.terminal ||
    active.timesheets ||
    active.absences ||
    active.issues ||
    active.infringements

  return {
    dashboardStatisticsModuleActive,
    activitiesHandbookIssuesActive,
    mainModuleActive,
  }
}

export interface DateRangeForm {
  dateStart: Date
  dateEnd: Date
}

export function useExportInfringementsForm(onCompleted?: () => void) {
  const translations = useTranslate({
    selectUsersError: 'activities.errors.select-multiple-users',
  })

  const me = useUser()

  const {
    exportInfringementsMass,
    exportInfringements,
    loading: mailLoading,
  } = useInfringementsMutations(onCompleted)

  const {
    formValues: form,
    formErrors: errors,
    updateForm,
    submitHandler: submit,
  } = useForm<InfringementsSettingsForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      useDateRange: false,
      exportOption: ExportOption.SAVE,
      exportFormat: ExportFormat.PDF,
      exportMass: false,
      user: '',
    },
    validators: {
      user: (value, form) =>
        !(value || form.exportMass) ? translations.selectUsersError : null,
    },
  })

  const handler = useCallback(
    <K extends keyof InfringementsSettingsForm>(key: K) => {
      return (value: InfringementsSettingsForm[K] | null) => {
        if (value === null) return
        updateForm({ [key]: value })
      }
    },
    [updateForm]
  )

  const handleExport = useCallback(
    async (values: InfringementsSettingsForm) => {
      const dateStart = form.useDateRange
        ? form.dateStart
        : startOfMonth(values.month)
      const dateEnd = form.useDateRange
        ? form.dateEnd
        : endOfMonth(values.month)

      if (form.user) {
        void exportInfringements({
          variables: {
            userMail: me.id,
            dateStart: dateStart.toISOString(),
            dateEnd: dateEnd.toISOString(),
            user: form.user,
            exportOption: values.exportOption,
          },
        })
      } else {
        void exportInfringementsMass({
          variables: {
            userMail: me.id,
            dateStart: dateStart.toISOString(),
            dateEnd: dateEnd.toISOString(),
            exportOption: values.exportOption,
            exportFormat: values.exportFormat,
          },
        })
      }
    },
    [
      exportInfringements,
      exportInfringementsMass,
      form.dateEnd,
      form.dateStart,
      form.useDateRange,
      form.user,
      me.id,
    ]
  )

  return {
    form,
    errors,
    loading: mailLoading,
    submit,
    updateForm,
    handleExport,
    handler,
  }
}

export const useSalaryModuleTranslations = () => {
  const translations = useTranslate({
    activities: 'common.activities',
    terminal: 'common.terminal',
    timesheets: 'common.timesheets',
  })

  function getModuleTranslation(moduleName: string) {
    switch (moduleName) {
      case Module.ACTIVITIES:
        return translations.activities
      case Module.TERMINAL:
        return translations.terminal
      case Module.TIMESHEETS:
        return translations.timesheets
      default:
        return ''
    }
  }

  return getModuleTranslation
}
